'use client';

import { useState } from 'react';
import type { VerseReference } from '@/lib/types';
import { TRANSLATION_OPTIONS } from '@/lib/constants';
import VerseHighlight from './VerseHighlight';

interface ChapterViewProps {
  book: string;
  chapter: number;
  verses: VerseReference[];
}

export default function ChapterView({ book, chapter, verses }: ChapterViewProps) {
  const [selectedVerse, setSelectedVerse] = useState<VerseReference | null>(null);
  const [translation, setTranslation] = useState('kjv');

  return (
    <div className="flex gap-6">
      {/* Chapter content */}
      <div className="flex-1">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2
            className="text-2xl font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            {book} {chapter}
          </h2>
          <select
            value={translation}
            onChange={(e) => setTranslation(e.target.value)}
            className="text-xs px-2 py-1.5 rounded-lg outline-none cursor-pointer"
            style={{
              backgroundColor: 'var(--bg-card)',
              color: 'var(--text-secondary)',
              border: '1px solid rgba(200, 164, 94, 0.15)',
            }}
          >
            {TRANSLATION_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Verses */}
        <div className="space-y-1">
          {verses.map((verse) => (
            <p
              key={verse.verse}
              className="text-sm leading-relaxed py-1 px-2 rounded cursor-pointer transition-colors"
              style={{
                backgroundColor:
                  selectedVerse?.verse === verse.verse
                    ? 'rgba(200, 164, 94, 0.08)'
                    : 'transparent',
              }}
              onClick={() =>
                setSelectedVerse(
                  selectedVerse?.verse === verse.verse ? null : verse
                )
              }
            >
              <span
                className="font-semibold mr-2 text-xs"
                style={{ color: 'var(--accent-gold)' }}
              >
                {verse.verse}
              </span>
              <span style={{ color: 'var(--text-primary)' }}>{verse.text}</span>
            </p>
          ))}
        </div>
      </div>

      {/* Selected verse detail */}
      {selectedVerse && (
        <div className="w-80 shrink-0">
          <VerseHighlight
            verse={selectedVerse}
            book={book}
            chapter={chapter}
            onClose={() => setSelectedVerse(null)}
          />
        </div>
      )}
    </div>
  );
}
